import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

const twoFactorSchema = z.object({
  code: z.string()
    .trim()
    .length(6, { message: "Le code doit contenir exactement 6 chiffres" })
    .regex(/^\d{6}$/, { message: "Le code doit contenir uniquement des chiffres" }),
});

interface TwoFactorModalProps {
  open: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

const TwoFactorModal = ({ open, onSuccess, onCancel }: TwoFactorModalProps) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();

  const handleVerify = async () => {
    // Validate input
    const validation = twoFactorSchema.safeParse({ code });
    if (!validation.success) {
      const errors = validation.error.errors.map(err => err.message).join(", ");
      setError(errors);
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Get current session to ensure we have a valid token
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setLoading(false);
        return;
      }

      const { data, error: verifyError } = await supabase.functions.invoke('verify-2fa', {
        body: { code, enable: false }
      });

      if (verifyError) {
        const status = (verifyError as any)?.context?.response?.status;
        const serverMessage = (verifyError as any)?.context?.response?.error || verifyError.message || "";

        // Erreur de rate limit explicite
        if (status === 429 || serverMessage.includes("Trop de tentatives") || verifyError.message?.includes("429")) {
          setError("Trop de tentatives échouées. Veuillez réessayer dans 15 minutes.");
        } else {
          // Pour tous les autres cas non 2xx, on affiche un message clair
          setError(serverMessage || "Code invalide. Veuillez réessayer.");
        }
        return;
      }

      if (data?.rateLimited) {
        setError("Trop de tentatives échouées. Veuillez réessayer dans 15 minutes.");
        return;
      }

      if (data?.valid) {
        toast({
          title: "Authentification réussie",
          description: data.usedBackupCode 
            ? "Code de secours utilisé avec succès" 
            : "Code 2FA vérifié avec succès",
        });
        onSuccess();
      } else {
        setError("Code invalide. Veuillez réessayer.");
      }
    } catch (error: any) {
      // Erreurs réseau ou autres issues client
      if (error.message?.includes('Trop de tentatives') || error.message?.includes('429')) {
        setError("Trop de tentatives échouées. Veuillez réessayer dans 15 minutes.");
      } else {
        setError(error.message || "Erreur lors de la vérification");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode("");
    setError("");
    onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" aria-describedby="2fa-description">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <DialogTitle>Authentification à deux facteurs</DialogTitle>
          </div>
          <DialogDescription id="2fa-description">
            Entrez le code à 6 chiffres de votre application d'authentification ou un code de secours
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Input
            type="text"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            className="text-center text-2xl tracking-widest"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && code.length === 6) {
                handleVerify();
              }
            }}
          />

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleVerify}
              disabled={loading || code.length !== 6}
              className="flex-1"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Vérification...
                </>
              ) : (
                "Vérifier"
              )}
            </Button>
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Annuler
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorModal;
